import { Component } from '@theme/component';

/**
 * <q-carousel> — the one slider for custom sections (hero today; any future
 * banner or logo row can reuse it).
 *
 * Swiping is native CSS scroll-snap on the track, so it works without JS.
 * This class adds: previous/next, progress indicators, a slide counter,
 * autoplay with a pause/play button, and an "active slide" state that CSS
 * uses for entrance and zoom animations.
 *
 * Markup contract (refs, Horizon's Component pattern):
 *   ref="track"       the scroll-snap container
 *   ref="slides[]"    each slide (direct children of the track)
 *   ref="prev"/"next" optional buttons      on:click="/previous" | "/next"
 *   ref="dots[]"      optional indicators   on:click="/select/N" (N = 0-based)
 *   ref="toggle"      optional pause/play   on:click="/togglePlay"
 *                     data-label-pause / data-label-play
 *   ref="counter"     optional "01 / 03" text
 *   data-autoplay="6" seconds per slide; omit or 0 for none
 *
 * Autoplay timing lives in CSS: the active indicator's fill animation lasts
 * --q-carousel-duration, and its `animationend` advances the slide. So the
 * progress bar and the slide change can never drift apart, and pausing is
 * just `animation-play-state: paused` (attribute [data-paused]).
 *
 * Host attributes for CSS:
 *   [data-playing]   autoplay is running (indicators animate)
 *   [data-paused]    temporarily held: pointer / focus inside, tab hidden
 *   slide[data-active] the slide in view (entrance + zoom animations)
 *
 * Slides change with a slow eased glide (GLIDE_MS; the browser's own smooth
 * scroll is a quick jump), the way Flipkart / Amazon phone banners move.
 *
 * Accessibility (WCAG 2.2.2): autoplay has a visible pause button (desktop),
 * holds while the pointer, a finger or keyboard focus is on it, stops when
 * the tab is hidden, and never runs with reduced motion. Indicators expose the current slide with aria-current;
 * inactive slides are inert. Works in RTL: scroll offsets are measured from
 * the first slide, so they are negative in RTL.
 *
 * @extends {Component<{ track: HTMLElement, slides: HTMLElement[], prev?: HTMLButtonElement, next?: HTMLButtonElement, dots?: HTMLButtonElement[], toggle?: HTMLButtonElement, counter?: HTMLElement }>}
 */
const GLIDE_MS = 700;

/** @param {number} t - 0…1 */
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

class Carousel extends Component {
  requiredRefs = ['track', 'slides'];

  /** requestAnimationFrame id of a running glide */
  #glide = 0;

  #index = 0;

  /** Shopper pressed pause: stays paused until they press play */
  #userPaused = false;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.style.setProperty('--q-carousel-duration', `${this.#seconds || 6}s`);

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.refs.track,
      threshold: 0.6,
    });
    for (const slide of this.refs.slides) this.#observer.observe(slide);

    this.addEventListener('pointerenter', this.#hold);
    this.addEventListener('pointerleave', this.#release);
    this.addEventListener('focusin', this.#hold);
    this.addEventListener('focusout', this.#release);
    this.addEventListener('animationend', this.#handleAnimationEnd);
    document.addEventListener('visibilitychange', this.#handleVisibility);
    this.#reducedMotion.addEventListener('change', this.#syncPlaying);
    // A finger on the banner takes over from a running glide
    this.refs.track.addEventListener('pointerdown', this.#stopGlide, { passive: true });

    this.#update();
    this.#syncPlaying();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#handleVisibility);
    this.#reducedMotion.removeEventListener('change', this.#syncPlaying);
    this.#stopGlide();
  }

  /** Index of the slide currently in view */
  get index() {
    return this.#index;
  }

  get count() {
    return this.refs.slides.length;
  }

  get #seconds() {
    return Number(this.dataset.autoplay) || 0;
  }

  /** Whether autoplay is allowed at all here (setting, slides, device, motion) */
  get #canAutoplay() {
    return this.#seconds > 0 && this.count > 1 && !this.#reducedMotion.matches;
  }

  next() {
    this.select((this.#index + 1) % this.count);
  }

  previous() {
    this.select((this.#index - 1 + this.count) % this.count);
  }

  /**
   * Scrolls the track (never the page) so slide `index` is in view.
   * @param {number} index - also the inline data of on:click="/select/N"
   */
  select(index) {
    const { track, slides } = this.refs;
    const slide = slides[index];
    const first = slides[0];
    if (!slide || !first) return;

    const left = slide.offsetLeft - first.offsetLeft;
    if (this.#reducedMotion.matches) {
      track.scrollTo({ left, behavior: 'instant' });
    } else {
      this.#glideTo(left);
    }
  }

  /**
   * Eased scroll of the track to `left`. Snapping is off while it runs (it
   * would catch every intermediate position) and back on at the end.
   * @param {number} left
   */
  #glideTo(left) {
    const { track } = this.refs;
    this.#stopGlide();
    const from = track.scrollLeft;
    const distance = left - from;
    if (Math.abs(distance) < 1) return;

    const start = performance.now();
    track.style.scrollSnapType = 'none';
    const step = (/** @type {number} */ now) => {
      const t = Math.min(1, (now - start) / GLIDE_MS);
      track.scrollLeft = from + distance * easeInOut(t);
      if (t < 1) {
        this.#glide = requestAnimationFrame(step);
      } else {
        this.#glide = 0;
        track.style.removeProperty('scroll-snap-type');
      }
    };
    this.#glide = requestAnimationFrame(step);
  }

  #stopGlide = () => {
    if (!this.#glide) return;
    cancelAnimationFrame(this.#glide);
    this.#glide = 0;
    this.refs.track.style.removeProperty('scroll-snap-type');
  };

  togglePlay() {
    this.#userPaused = !this.#userPaused;
    this.#syncPlaying();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.refs.slides.indexOf(/** @type {HTMLElement} */ (entry.target));
      if (index !== -1 && index !== this.#index) {
        this.#index = index;
        this.#update();
      }
    }
  };

  /** The active indicator's fill finished: time for the next slide */
  #handleAnimationEnd = (/** @type {AnimationEvent} */ event) => {
    if (!this.hasAttribute('data-playing') || this.hasAttribute('data-paused')) return;
    const activeDot = this.refs.dots?.[this.#index];
    if (activeDot && activeDot.contains(/** @type {Node} */ (event.target)) && event.animationName === 'q-carousel-progress') {
      this.next();
    }
  };

  /** Syncs active slide, indicators, counter and assistive-tech state */
  #update() {
    this.refs.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.refs.dots?.forEach((dot, i) => {
      if (i === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (this.refs.counter) {
      const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
      this.refs.counter.textContent = `${pad(this.#index + 1)} / ${pad(this.count)}`;
    }
  }

  #syncPlaying = () => {
    const playing = this.#canAutoplay && !this.#userPaused;
    this.toggleAttribute('data-playing', playing);

    const { toggle } = this.refs;
    if (toggle) {
      toggle.hidden = !this.#canAutoplay;
      toggle.setAttribute('aria-pressed', String(this.#userPaused));
      toggle.setAttribute('aria-label', (this.#userPaused ? this.dataset.labelPlay : this.dataset.labelPause) ?? '');
    }
  };

  #hold = () => {
    this.toggleAttribute('data-paused', true);
  };

  #release = (/** @type {Event} */ event) => {
    // Focus moving between two controls inside the carousel is not "leaving"
    if (event instanceof FocusEvent && this.contains(/** @type {Node | null} */ (event.relatedTarget))) return;
    this.removeAttribute('data-paused');
  };

  #handleVisibility = () => {
    this.toggleAttribute('data-paused', document.hidden || this.matches(':hover, :focus-within'));
  };
}

if (!customElements.get('q-carousel')) {
  customElements.define('q-carousel', Carousel);
}
